// apps/web/src/features/auth/consentPolicy.ts
//
// [프로토타입] app_id별 약관 동의 항목 정책.
//
// 현재는 프론트에만 있는 하드코딩 테이블이다. 서버(auth-service)는 아직
// app_id를 모르고 terms_version/privacy_version을 무조건 요구하므로
// (auth.js:256 AGREEMENTS_REQUIRED), 필수 2종을 빼는 정책은 서버가 400으로
// 막는다. 서버 분기가 붙기 전까지는 "항목을 더 보여주는" 방향만 실동작한다.
//
// 정식 구현 시 이 테이블은 apps 테이블(app_id, signup_consent_required,
// terms_version, privacy_version)로 옮기고 서버에서 내려받는다.

export type ConsentItem = {
    /** user_consents.code 로 저장될 값 */
    code: string;
    label: string;
    required: boolean;
    /** 필수 항목은 버전이 있어야 서버 검증을 통과한다 */
    version?: string;
    /** 전문보기 라우트. 없으면 버튼을 안 그린다 */
    legalPath?: string;
    /** 항목 우측 회색 보조문구 */
    note?: string;
};

export type ConsentPolicy = {
    app_id: string;
    name: string;
    items: ConsentItem[];
};

const TERMS_VERSION   = import.meta.env.VITE_TERMS_VERSION   ?? "2025-09-01";
const PRIVACY_VERSION = import.meta.env.VITE_PRIVACY_VERSION ?? "2025-09-01";

export const DEFAULT_APP_ID = "booktalk";

/** app_id 를 세션에 보관하는 키. /legal/* 를 다녀오면 쿼리가 날아가서 필요하다. */
export const APP_ID_SS_KEY = "bt.app_id";

const TERMS_ITEM: ConsentItem = {
    code: "terms",
    label: "서비스 이용약관 동의",
    required: true,
    version: TERMS_VERSION,
    legalPath: "/legal/terms",
};

const PRIVACY_ITEM: ConsentItem = {
    code: "privacy",
    label: "개인정보처리방침 동의",
    required: true,
    version: PRIVACY_VERSION,
    legalPath: "/legal/privacy",
};

// PageBox: 약관 전문 페이지가 아직 없다.
//
// legalPath 를 비우면 "전문보기" 버튼이 렌더되지 않는다(AgreeTermsPage 의 renderRow).
// 대신 note 로 안내만 띄운다.
//
// version 은 반드시 채워야 한다. 서버 /register 가 terms_version/privacy_version 이
// 비어 있으면 400 AGREEMENTS_REQUIRED 로 막는다(auth.js:256) — legalPath 처럼
// 그냥 지우면 가입 자체가 안 된다.
//
// ※ 임시 상태다. 사용자가 내용을 읽을 방법이 없는 채로 동의를 받는 것이므로
//    전문 페이지가 준비되면 legalPath 를 채우고 note 를 지울 것.
const PAGEBOX_TERMS_VERSION   = import.meta.env.VITE_PAGEBOX_TERMS_VERSION   ?? "1.0";
const PAGEBOX_PRIVACY_VERSION = import.meta.env.VITE_PAGEBOX_PRIVACY_VERSION ?? "1.0";

const POLICIES: Record<string, ConsentPolicy> = {
    pagebox: {
        app_id: "pagebox",
        name: "PageBox",
        items: [
            {
                code: "terms",
                label: "서비스 이용약관 동의",
                required: true,
                version: PAGEBOX_TERMS_VERSION,
                note: "전문 준비 중",
            },
            {
                code: "privacy",
                label: "개인정보처리방침 동의",
                required: true,
                version: PAGEBOX_PRIVACY_VERSION,
                note: "전문 준비 중",
            },
        ],
    },

    // 현행 독서노트. 지금 화면에 보이는 것과 동일하게 유지한다.
    booktalk: {
        app_id: "booktalk",
        name: "독서노트",
        items: [TERMS_ITEM, PRIVACY_ITEM],
    },
};

export type ResolvedPolicy = {
    policy: ConsentPolicy;
    /** 요청한 app_id 가 테이블에 없어서 기본값으로 떨어졌는지 */
    fellBack: boolean;
    requestedAppId: string;
};

/**
 * app_id → 정책. 모르는 app_id 는 기본 정책(필수 전체 노출)으로 떨어뜨린다.
 * 항목을 덜 보여주는 쪽으로 실패하면 안 되므로 fail-closed 가 아니라
 * "가장 많이 받는 쪽"이 안전한 기본값이다.
 */
export function resolveConsentPolicy(appId: string | null | undefined): ResolvedPolicy {
    const requested = String(appId || "").trim();
    const hit = requested ? POLICIES[requested] : undefined;

    if (hit) return { policy: hit, fellBack: false, requestedAppId: requested };

    return {
        policy: POLICIES[DEFAULT_APP_ID],
        fellBack: !!requested,
        requestedAppId: requested || DEFAULT_APP_ID,
    };
}

/**
 * 체크 상태 → /register 로 보낼 agreements.
 *
 * terms_version/privacy_version 은 서버가 필수로 보는 기존 계약이라 그대로 둔다.
 * app_id 와 consents 는 서버가 아직 안 읽는 확장 필드다(무시된다).
 */
export function buildAgreements(policy: ConsentPolicy, checked: Record<string, boolean>) {
    const find = (code: string) => policy.items.find((i) => i.code === code);

    return {
        // ── 기존 계약 (auth-service 가 검증하는 필드) ──
        terms_version:   find("terms")?.version   ?? "",
        privacy_version: find("privacy")?.version ?? "",
        marketing_sms:   !!checked["marketing_sms"],

        // ── 확장 필드 (서버 분기 구현 시 사용) ──
        app_id: policy.app_id,
        consents: policy.items.map((i) => ({
            code: i.code,
            version: i.version ?? null,
            required: i.required,
            agreed: !!checked[i.code],
        })),
    };
}